// Owner: ui-layers (wave 1), shell-nav (wave 2). Root layout: the one layer stack (LayerRoot), the UI
// kit's language, the global overlays (src/overlays/*.tsx, lazy), the interaction kit (keyboard
// commands, server undo, clipboard; interaction-kit), the one navigation guard (dirty layers and
// dirty page scopes, lib/dirty.ts), document titles, and the 404 and error states of every route
// that has none of its own (inside the shell for signed-in members).
import type { QueryClient } from '@tanstack/react-query';
import { Outlet, createRootRouteWithContext, useRouter } from '@tanstack/react-router';
import { Suspense, lazy, useEffect, useMemo } from 'react';
import {
  type AccessContactsSource,
  AccessContactsProvider,
  type LayerNavigationSource,
  LayerRoot,
  UiI18nProvider,
  setUiLocale,
} from '@dataroom/ui';
import { captureEarlyKeys } from '@dataroom/interaction/early';
import { getLocale } from '../lib/i18n';
import {
  GlobalLayerHost,
  type OverlayModule,
  registerOverlayModules,
  setLayerFallbackNavigate,
} from '../shell/overlays';
import { PageError, RootNotFound, TitleHost } from '../shell/states';

// Overlay definitions are light (lazy components + tiny effects); `*.layer.tsx` files hold the heavy
// parts and load on first open.
const overlayModules = import.meta.glob<{ default: OverlayModule }>(
  ['../overlays/*.tsx', '!../overlays/*.layer.tsx'],
  { eager: true },
);
registerOverlayModules(Object.values(overlayModules).map((m) => m.default));
// Keyboard commands, server undo, clipboard routing and the navigation guard (interaction-kit) load
// in parallel with the first route, beside the app (not part of the first-paint bundle).
captureEarlyKeys();
const interactionHost = import('../extensions/InteractionHost');
const InteractionHost = lazy(() => interactionHost);
// Toasts render outside the router (main.tsx): give the kit the member's language before any render.
setUiLocale(getLocale());

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  component: Root,
  notFoundComponent: RootNotFound,
  errorComponent: PageError,
});

function Root() {
  const router = useRouter();
  const { queryClient } = Route.useRouteContext();
  const locale = getLocale();
  // Every NoAccess names the workspace admins (read when one renders; the loader stays out of the
  // first paint).
  const accessContacts = useMemo<AccessContactsSource>(
    () => () => import('../lib/access-contacts').then((m) => m.workspaceAdminsSource(queryClient)()),
    [queryClient],
  );

  // Layers close on a pathname change or a browser back / forward (not on search-only updates such
  // as a view draft in the URL).
  const navigation = useMemo<LayerNavigationSource>(
    () => ({
      subscribe(listener) {
        let { pathname, href } = router.history.location;
        return router.history.subscribe(({ location, action }) => {
          const pathChanged = location.pathname !== pathname;
          const hrefChanged = location.href !== href;
          pathname = location.pathname;
          href = location.href;
          // A traversal the dirty guard held back lands on the same entry again: nothing closes.
          if (!hrefChanged) return;
          listener({
            kind: action.type === 'PUSH' ? 'push' : action.type === 'REPLACE' ? 'replace' : 'traverse',
            pathChanged,
          });
        });
      },
    }),
    [router],
  );

  useEffect(() => {
    setLayerFallbackNavigate(({ pathname }) => void router.navigate({ to: pathname }));
    return () => setLayerFallbackNavigate(null);
  }, [router]);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  return (
    <UiI18nProvider locale={locale}>
      <AccessContactsProvider source={accessContacts}>
        <LayerRoot navigation={navigation}>
          <Outlet />
          <TitleHost />
          <GlobalLayerHost />
          <Suspense fallback={null}>
            <InteractionHost queryClient={queryClient} />
          </Suspense>
        </LayerRoot>
      </AccessContactsProvider>
    </UiI18nProvider>
  );
}
